import { supabase } from "@/lib/supabase";
import { Sentry } from "@/lib/sentry";
import type { VenueOnboardingFormValues } from "./schema";
import { NO_BOOKING_SYSTEM } from "./schema";

export interface SubmitResult {
  applicationId: string;
}

/**
 * Submits a venue application to Supabase. Thin service-layer wrapper: all
 * external calls live here so the UI never touches Supabase directly.
 */
export async function submitVenueApplication(
  values: VenueOnboardingFormValues
): Promise<SubmitResult> {
  const bookingSystem =
    values.uses_booking_system === "no"
      ? NO_BOOKING_SYSTEM
      : (values.booking_system ?? "").trim();

  // Generate the application ID client-side. This lets us skip the
  // INSERT ... RETURNING round-trip, which would otherwise require a matching
  // SELECT policy for the anon role (we only grant SELECT to admins). Postgres
  // reports missing-SELECT-on-RETURNING as a misleading 42501 "new row violates
  // row-level security policy" error, hence going through the client.
  const applicationId =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : // Fallback for ancient runtimes: v4-ish shape from Math.random. Not
        // cryptographically strong but fine as a public-facing reference ID.
        "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          const v = c === "x" ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        });

  const { error } = await supabase.from("venue_applications").insert({
    id: applicationId,
    venue_name: values.venue_name,
    country: values.country,
    address_line1: values.address_line1,
    address_line2: values.address_line2 || null,
    city: values.city,
    region: values.region,
    postcode: values.postcode,
    contact_name: values.contact_name,
    contact_email: values.contact_email,
    contact_phone: values.contact_phone,
    booking_system: bookingSystem,
    booking_system_other: null,
    courts: [],
    notes: values.notes || null,
  });

  if (error) {
    Sentry.captureException(error, {
      tags: { feature: "venue_onboarding" },
      extra: { venueName: values.venue_name, country: values.country },
    });
    throw new Error(
      "We couldn't submit your application right now. Please try again in a moment."
    );
  }

  return { applicationId };
}
